'use client'

import React, { useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { parseAndSplitByH2 } from '@/lib/parseMarkdown';
import { buildChunkMeta } from '@/lib/chunkMeta';
import { LocaleCode, localeStrings } from './config';

// Builds the race/slowdown ending views for a translated version of the
// scenario, mirroring app/(mainPage)/race/race.tsx and slowdown/slowdown.tsx.
export function makeEndingViews({
  locale,
  narrativeMd,
  raceMd,
  slowdownMd,
  ArticleContent,
}: {
  locale: LocaleCode;
  narrativeMd: string;
  raceMd: string;
  slowdownMd: string;
  ArticleContent: React.ComponentType<any>;
}) {
  const strings = localeStrings[locale];
  const narrativeCount = parseAndSplitByH2(narrativeMd).length;

  const makeView = (ending: 'race' | 'slowdown', markdown: string) => {
    const contentSections = buildChunkMeta(
      parseAndSplitByH2(markdown),
      narrativeCount,
      strings.endingTitles
    );
    const otherEnding = ending === 'race' ? 'slowdown' : 'race';

    const EndingView = () => {
      const router = useRouter();
      useEffect(() => {
        if (ending !== 'race') return;
        document.body.classList.add('race');
        return () => {
          document.body.classList.remove('race');
        };
      }, []);
      return (
        <ArticleContent
          key={ending}
          contentSections={contentSections}
          gridAreas={{
            graph: 'graph2',
            graphgap: 'graphgap2',
            content: 'choice',
            toc: 'toc2',
          }}
          name={ending}
          startingIndex={narrativeCount}
          className={"animate-revealDown mt-[200px]"}
          bottomElement={
            <div>
              <Link href={`/${locale}/${ending}#section-${ending}-0`}>
                <button
                  className="px-4 py-2 rounded bg-[var(--accent)] text-white"
                  // Scroll to the top of the page before changing the ending
                  onClick={() => setTimeout(() => router.push(`/${locale}/${otherEnding}`), 100)}
                >
                  {strings.readOtherEnding}
                </button>
              </Link>
            </div>
          }
        />
      );
    };
    return EndingView;
  };

  return {
    RaceView: makeView('race', raceMd),
    SlowdownView: makeView('slowdown', slowdownMd),
  };
}
